import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import {
  getLiveInternationalMedals,
  getLiveNationalMedals,
} from "../../utils/medals";
import { FaTrophy, FaMedal, FaUsers, FaGlobeAsia, FaBriefcase, FaChartLine, FaBullseye, FaHandHoldingHeart, FaFlag, FaNewspaper, FaImages, FaBroadcastTower, FaFileAlt } from "react-icons/fa";

// --- Reusable Components ---
const normalizeImageFit = (fit) => {
  const value = String(fit || "cover").toLowerCase();
  if (["contain", "contain_width", "contain_height"].includes(value)) return value;
  return "cover";
};
const normalizeImageSize = (size) => {
  const value = String(size || "medium").toLowerCase();
  if (["small", "medium", "large", "xlarge"].includes(value)) return value;
  return "medium";
};
const normalizeSliderFitMode = (fitMode) => {
  const value = String(fitMode || "cover").toLowerCase();
  if (value === "contain" || value === "auto") return value;
  return "cover";
};
const imageSizeClasses = (size) => {
  const normalized = normalizeImageSize(size);
  if (normalized === "small") {
    return {
      container: "min-h-[180px]",
      horizontalItem: "h-40 w-56",
      verticalItem: "h-44",
      gridItem: "h-24 sm:h-28",
      sliderWrapper: "max-w-[560px]",
      sliderMain: "aspect-[16/9]",
      thumb: "h-16",
    };
  }
  if (normalized === "large") {
    return {
      container: "min-h-[260px]",
      horizontalItem: "h-64 w-96",
      verticalItem: "h-72",
      gridItem: "h-40 sm:h-48",
      sliderWrapper: "max-w-[980px]",
      sliderMain: "aspect-[16/9]",
      thumb: "h-28",
    };
  }
  if (normalized === "xlarge") {
    return {
      container: "min-h-[300px]",
      horizontalItem: "h-72 w-[28rem]",
      verticalItem: "h-80",
      gridItem: "h-44 sm:h-56",
      sliderWrapper: "max-w-[1180px]",
      sliderMain: "aspect-[16/9]",
      thumb: "h-32",
    };
  }
  return {
    container: "min-h-[220px]",
    horizontalItem: "h-52 w-72",
    verticalItem: "h-56",
    gridItem: "h-32 sm:h-36",
    sliderWrapper: "max-w-[760px]",
    sliderMain: "aspect-[16/9]",
    thumb: "h-20",
  };
};

const imageFitClasses = (fit) => {
  const mode = normalizeImageFit(fit);
  if (mode === "contain") return { frame: "bg-slate-100", image: "block w-full h-full object-contain" };
  if (mode === "contain_width") return { frame: "bg-slate-100", image: "block w-full h-full object-contain object-top" };
  if (mode === "contain_height") return { frame: "bg-slate-100", image: "block w-full h-full object-contain object-center" };
  return { frame: "", image: "block w-full h-full object-cover" };
};

const normalizeSectionKey = (value) => String(value || "").trim().toLowerCase();

const isHomeReservedSectionKey = (key) => {
  const normalized = normalizeSectionKey(key);
  if (!normalized) return false;
  if (/^why_join_/i.test(normalized)) return true;
  if (/^home_(hero|impact|dsc|policy|about|pride|timeline|leadership|champions|initiatives|vision|media|cta)$/i.test(normalized)) return true;
  if (/^home_(timeline_item|leadership_card|champion_card|initiative_card|vision_point|impact_card)_/i.test(normalized)) return true;
  if (/^home_(dsc|policy|pride|timeline|leadership|champions|initiatives|vision|media|cta)_subsection_/i.test(normalized)) return true;
  return false;
};

const isHomeGenericSectionKey = (key) => {
  const normalized = normalizeSectionKey(key);
  if (!normalized.startsWith("home_")) return false;
  return !isHomeReservedSectionKey(normalized);
};

const summarizeText = (text, maxChars = 260) => {
  const value = String(text || "").trim().replace(/\s+/g, " ");
  if (!value) return "";
  if (value.length <= maxChars) return value;

  const sliced = value.slice(0, maxChars);
  const boundary = Math.max(sliced.lastIndexOf(". "), sliced.lastIndexOf(" "));
  const compact = boundary > 80 ? sliced.slice(0, boundary) : sliced;
  return `${compact.trim()}...`;
};

const StatCard = ({ icon, value, label }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg text-center transform hover:-translate-y-2 transition-transform duration-300">
    <div className="text-blue-600 mb-3">{icon}</div>
    <div className="text-4xl font-bold text-slate-900">{value}</div>
    <div className="text-slate-500 mt-1">{label}</div>
  </div>
);

StatCard.propTypes = {
  icon: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
};

const ChampionCard = ({
  name,
  achievement,
  imageSrc,
  imageSide = "left",
  headingAlign = "left",
  textAlign = "left",
  imageFit = "cover",
  variant = "split",
}) => {
  const fitClasses = imageFitClasses(imageFit);
  const headingAlignClass =
    headingAlign === "right"
      ? "text-right"
      : headingAlign === "center"
        ? "text-center"
        : "text-left";
  const textAlignClass =
    textAlign === "right" ? "text-right" : textAlign === "center" ? "text-center" : "text-left";

  if (variant === "stack") {
    return (
      <article className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100 h-full">
        <div className="p-5 md:p-6">
          <h3 className="text-2xl font-bold text-slate-900 mb-4 text-center">{name}</h3>
          <div className={`w-full h-64 md:h-72 rounded-xl flex items-center justify-center overflow-hidden ${fitClasses.frame}`}>
            <img
              src={imageSrc}
              alt={name}
              className={fitClasses.image}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/images/champion-ravi.jpg";
              }}
            />
          </div>
          <p className="text-slate-600 mt-4 leading-relaxed text-center">{achievement}</p>
        </div>
      </article>
    );
  }

  return (
    <article className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100">
    <div
      className={`flex flex-col ${
        imageSide === "right" ? "lg:flex-row-reverse" : "lg:flex-row"
      }`}
    >
      <div className={`lg:w-5/12 w-full h-64 md:h-72 lg:h-[300px] flex items-center justify-center shrink-0 ${fitClasses.frame}`}>
        <img
          src={imageSrc}
          alt={name}
          className={fitClasses.image}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/images/champion-ravi.jpg";
          }}
        />
      </div>
      <div className={`lg:w-7/12 p-6 md:p-8 ${textAlignClass}`}>
        <h3
          className={`text-2xl md:text-3xl font-bold text-slate-900 ${headingAlignClass}`}
        >
          {name}
        </h3>
        <p className="text-slate-600 mt-3 leading-relaxed">{achievement}</p>
      </div>
    </div>
  </article>
  );
};

ChampionCard.propTypes = {
  name: PropTypes.string.isRequired,
  achievement: PropTypes.string.isRequired,
  imageSrc: PropTypes.string.isRequired,
  imageSide: PropTypes.oneOf(["left", "right"]),
  headingAlign: PropTypes.oneOf(["left", "center", "right"]),
  textAlign: PropTypes.oneOf(["left", "center", "right"]),
  imageFit: PropTypes.oneOf(["cover", "contain", "contain_width", "contain_height"]),
  variant: PropTypes.oneOf(["split", "stack"]),
};

const InitiativeCard = ({ title, description, icon, link, imageSrc, imageFit = "cover" }) => {
  const fitClasses = imageFitClasses(imageFit);
  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {imageSrc ? (
        <div className={`w-full overflow-hidden ${fitClasses.frame}`} style={{ aspectRatio: "16 / 9" }}>
          <img
            src={imageSrc}
            alt={title || "Initiative image"}
            className={fitClasses.image}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "https://placehold.co/1200x700?text=Initiative";
            }}
          />
        </div>
      ) : null}
      <div className="p-8 flex items-start gap-6">
        <div className="text-blue-600 flex-shrink-0">{icon}</div>
        <div>
          <h3 className="text-2xl font-bold text-slate-900">{title}</h3>
          <p className="text-slate-600 mt-2 mb-4">{description}</p>
          {link && (
            <Link to={link} className="text-blue-600 font-semibold hover:underline flex items-center gap-2">
              Read More <span>&rarr;</span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

InitiativeCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  link: PropTypes.string,
  imageSrc: PropTypes.string,
  imageFit: PropTypes.oneOf(["cover", "contain", "contain_width", "contain_height"]),
};

const LeadershipCard = ({ name, role, message, imageSrc }) => (
  <div className="bg-white p-8 rounded-2xl shadow-lg flex flex-col items-center text-center hover:-translate-y-1 transition-transform duration-300 border-t-4 border-blue-600">
    <div className="w-32 h-32 rounded-full overflow-hidden mb-6 border-4 border-slate-100 shadow-sm">
      <img
        src={imageSrc}
        alt={name}
        className="w-full h-full object-cover"
      />
    </div>
    <h3 className="text-xl font-bold text-slate-900">{name}</h3>
    <p className="text-blue-600 font-bold text-sm tracking-wider mb-4 uppercase">{role}</p>
    <p className="text-slate-600 text-sm leading-relaxed italic line-clamp-6">"{message}"</p>
  </div>
);

LeadershipCard.propTypes = {
  name: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  imageSrc: PropTypes.string.isRequired,
};

const SectionImageGallery = ({
  images = [],
  layout = "auto",
  fit = "cover",
  size = "medium",
  alt = "Section image",
  fallbackSrc = "https://placehold.co/1200x700?text=Home+Section",
}) => {
  const normalizedImages = useMemo(
    () => (Array.isArray(images) && images.length ? images : [fallbackSrc]),
    [images, fallbackSrc]
  );
  const normalizedLayout = String(layout || "auto").toLowerCase();
  const imagesSignature = useMemo(() => normalizedImages.join("|"), [normalizedImages]);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeImage = normalizedImages[Math.min(activeIndex, normalizedImages.length - 1)] || fallbackSrc;
  const hasMultiple = normalizedImages.length > 1;
  const resolvedLayout = useMemo(() => {
    if (!hasMultiple) return "single";
    if (["slider", "horizontal", "vertical", "grid2", "grid3"].includes(normalizedLayout)) {
      return normalizedLayout;
    }
    if (normalizedLayout === "auto") {
      return normalizedImages.length <= 2 ? "grid2" : "slider";
    }
    return "slider";
  }, [hasMultiple, normalizedLayout, normalizedImages.length]);
  const fitClasses = imageFitClasses(fit);
  const sizeClasses = imageSizeClasses(size);

  useEffect(() => {
    setActiveIndex(0);
  }, [imagesSignature]);

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + normalizedImages.length) % normalizedImages.length);
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % normalizedImages.length);
  };

  if (resolvedLayout === "horizontal") {
    return (
      <div className={`rounded-t-2xl overflow-hidden shadow bg-white ${sizeClasses.container} p-3`}>
        <div className="flex gap-3 overflow-x-auto">
          {normalizedImages.map((src, idx) => (
            <div key={`${src}-${idx}`} className={`${sizeClasses.horizontalItem} shrink-0 rounded-lg flex items-center justify-center overflow-hidden ${fitClasses.frame}`}>
              <img
                src={src}
                alt={`${alt || "Section image"} ${idx + 1}`}
                className={fitClasses.image}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (resolvedLayout === "vertical") {
    return (
      <div className={`rounded-t-2xl overflow-hidden shadow bg-white ${sizeClasses.container} p-3 space-y-3`}>
        {normalizedImages.map((src, idx) => (
          <div key={`${src}-${idx}`} className={`w-full ${sizeClasses.verticalItem} rounded-lg flex items-center justify-center overflow-hidden ${fitClasses.frame}`}>
            <img
              src={src}
              alt={`${alt || "Section image"} ${idx + 1}`}
              className={fitClasses.image}
            />
          </div>
        ))}
      </div>
    );
  }

  if (resolvedLayout === "grid2" || resolvedLayout === "grid3") {
    const colsClass = resolvedLayout === "grid3" ? "sm:grid-cols-3" : "sm:grid-cols-2";
    return (
      <div className={`rounded-t-2xl overflow-hidden shadow bg-white ${sizeClasses.container} p-3`}>
        <div className={`grid grid-cols-2 ${colsClass} gap-3`}>
          {normalizedImages.map((src, idx) => (
            <div key={`${src}-${idx}`} className={`w-full ${sizeClasses.gridItem} rounded-lg flex items-center justify-center overflow-hidden ${fitClasses.frame}`}>
              <img
                src={src}
                alt={`${alt || "Section image"} ${idx + 1}`}
                className={fitClasses.image}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-t-2xl overflow-hidden shadow bg-slate-200 ${sizeClasses.container}`}>
      <div className={`w-full mx-auto ${sizeClasses.sliderWrapper || ""}`}>
        <div className={`relative w-full ${sizeClasses.sliderMain} flex items-center justify-center overflow-hidden ${fitClasses.frame}`}>
          <img
            src={activeImage}
            alt={alt || "Section image"}
            className={fitClasses.image}
          />
          {hasMultiple ? (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/55 text-white hover:bg-black/70 transition-colors"
                aria-label="Previous photo"
              >
                &#8249;
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/55 text-white hover:bg-black/70 transition-colors"
                aria-label="Next photo"
              >
                &#8250;
              </button>
              <div className="absolute bottom-2 right-2 bg-black/65 text-white text-xs px-2 py-1 rounded">
                {activeIndex + 1}/{normalizedImages.length}
              </div>
            </>
          ) : null}
        </div>
        {hasMultiple ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-white">
            {normalizedImages.map((src, idx) => (
              <button
                key={`${src}-${idx}`}
                type="button"
                onClick={() => setActiveIndex(idx)}
                className={`overflow-hidden rounded border-2 transition-colors ${
                  idx === activeIndex ? "border-blue-600" : "border-transparent"
                }`}
                aria-label={`Show photo ${idx + 1}`}
              >
                <img
                  src={src}
                  alt={`${alt || "Section image"} ${idx + 1}`}
                  className={`w-full ${sizeClasses.thumb} object-cover`}
                />
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

SectionImageGallery.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string),
  layout: PropTypes.string,
  fit: PropTypes.oneOf(["cover", "contain", "contain_width", "contain_height"]),
  size: PropTypes.oneOf(["small", "medium", "large", "xlarge"]),
  alt: PropTypes.string,
  fallbackSrc: PropTypes.string,
};

const SectionSubsectionGrid = ({ items = [] }) => {
  if (!Array.isArray(items) || !items.length) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
      {items.map((item, idx) => (
        <article key={`${item.sectionKey}-${idx}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {item.image ? (
            <img
              src={item.image}
              alt={item.heading || `Subsection ${idx + 1}`}
              className="w-full h-44 object-cover"
            />
          ) : null}
          <div className="p-5">
            {item.subheading ? (
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">{item.subheading}</p>
            ) : null}
            {item.heading ? (
              <h3 className="text-xl font-bold text-slate-900 mt-1">{item.heading}</h3>
            ) : null}
            {item.body ? (
              <p className="text-slate-600 mt-2 leading-relaxed whitespace-pre-line">{item.body}</p>
            ) : null}
            {item.ctaLink ? (
              <a
                href={item.ctaLink}
                target={item.ctaLink.startsWith("/") ? undefined : "_blank"}
                rel={item.ctaLink.startsWith("/") ? undefined : "noreferrer"}
                className="inline-block mt-3 text-blue-700 font-semibold hover:underline"
              >
                {item.ctaLabel || "Read More"} &rarr;
              </a>
            ) : null}
          </div>
        </article>
      ))}
    </div>
  );
};

const parsePublicEventDate = (item) => {
  const raw = item?.date || item?.event_date || item?.event_end_date || item?.end_date || null;
  if (!raw) return new Date(0);
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
};

const formatShortDate = (raw) => {
  if (!raw) return "Date TBD";
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

const formatShortEventDateRange = (startRaw, endRaw) => {
  const start = startRaw || null;
  const end = endRaw || startRaw || null;
  if (!start && !end) return "Date TBD";
  if (!start) return formatShortDate(end);
  if (!end || end === start) return formatShortDate(start);
  return `${formatShortDate(start)} to ${formatShortDate(end)}`;
};

SectionSubsectionGrid.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      sectionKey: PropTypes.string,
      heading: PropTypes.string,
      subheading: PropTypes.string,
      body: PropTypes.string,
      image: PropTypes.string,
      ctaLabel: PropTypes.string,
      ctaLink: PropTypes.string,
    })
  ),
};

const Home = () => {
  const [activities, setActivities] = useState([]);
  const [homeSections, setHomeSections] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [medalSummary, setMedalSummary] = useState(null);
  const [publicEvents, setPublicEvents] = useState([]);
  const [updatesSections, setUpdatesSections] = useState([]);

  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        const [activityRes, sectionRes, sliderRes, medalRes, eventRes, updatesRes] = await Promise.all([
          API.fetchActivities(),
          API.fetchAboutSections("home"),
          API.fetchSlider(),
          API.fetchHomeMedalSummary(),
          API.fetchEvents(),
          API.fetchAboutSections("updates_results"),
        ]);
        setActivities(activityRes || []);
        setHomeSections(Array.isArray(sectionRes) ? sectionRes : []);
        setSliders(Array.isArray(sliderRes) ? sliderRes : []);
        setMedalSummary(medalRes || null);
        setPublicEvents(Array.isArray(eventRes?.data) ? eventRes.data : []);
        setUpdatesSections(Array.isArray(updatesRes) ? updatesRes : []);
      } catch (err) {
        console.error("Error loading homepage data:", err);
        setActivities([]);
        setHomeSections([]);
        setSliders([]);
        setMedalSummary(null);
        setPublicEvents([]);
        setUpdatesSections([]);
      }
    };
    fetchHomepageData();
  }, []);

  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const medalRes = await API.fetchHomeMedalSummary();
        setMedalSummary(medalRes || null);
      } catch (err) {
        console.error("Error refreshing medal summary:", err);
      }
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!Array.isArray(sliders) || sliders.length <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliders.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [sliders]);

  const activeSlider = sliders[currentSlide] || null;
  const currentYear = String(new Date().getFullYear());
  const liveNationalMedals = getLiveNationalMedals(medalSummary);
  const liveInternationalMedals = getLiveInternationalMedals(medalSummary);
  const fallbackWhyJoinCards = [
    {
      title: "Professional Coaching",
      description: "Train with structured programs, experienced mentors, and discipline-specific guidance.",
      icon: <FaBullseye size={32} />,
    },
    {
      title: "Competition Pathway",
      description: "Move from district events to state, national, and international platforms with the right support.",
      icon: <FaChartLine size={32} />,
    },
    {
      title: "Recognition & Opportunities",
      description: "Earn medals, visibility, career opportunities, and government support through merit.",
      icon: <FaMedal size={32} />,
    },
    {
      title: "Inclusive Community",
      description: "Join a strong ecosystem that values resilience, dignity, and equal opportunity for every athlete.",
      icon: <FaHandHoldingHeart size={32} />,
    },
  ];
  const fallbackTimelineItems = [
    { year: "2015", count: 7, label: "" },
    { year: "2016", count: 2, label: "" },
    { year: "2017", count: 45, label: "" },
    { year: "2018", count: 5, label: "" },
    { year: "2019", count: 10, label: "" },
    { year: "2021", count: 65, label: "" },
    { year: "2022", count: 65, label: "" },
    { year: "2023", count: 117, label: "" },
    { year: "2024", count: 68, label: "" },
    { year: "2025", count: 128, label: "" },
    { year: "2026", count: 13, label: "" },
  ];
  const fallbackLeadershipCards = [
    {
      name: "Gonuguntla Koteswara Rao",
      role: "President",
      message:
        "This is not the end - it is the beginning of a larger mission. We will expand to every district, train more coaches, and set our sights on Paralympic podium finishes.",
      imageSrc: "/images/president.jpg",
    },
    {
      name: "V. Rama Swamy",
      role: "Secretary",
      message:
        "My vision is simple - no divyang athlete should have to fight two battles: one on the field and one for their basic rights. We are building a system that protects their future.",
      imageSrc: "/images/secretary.jpg",
    },
    {
      name: "P Paidamma",
      role: "Treasurer",
      message:
        "Our focus has always been to ensure that funds reach the athletes where they are needed most. Every medal has behind it careful planning and resource management.",
      imageSrc: "/images/treasurer.jpg",
    },
  ];
  const fallbackChampionCards = [
    {
      name: "Rongali Ravi",
      achievement:
        "5th Place at Paris Paralympics 2024 & Asian Games Silver Medalist",
      imageSrc: "/images/champion-ravi.jpg",
    },
    {
      name: "Konganapalli Narayana",
      achievement: "12th Place at Paris Paralympics 2024 (Para Rowing)",
      imageSrc: "/images/champion-narayana.jpg",
    },
    {
      name: "Killaka Lalitha",
      achievement: "Asian Games Silver Medalist (1500m) - T11 Category",
      imageSrc: "/images/champion-lalitha.jpg",
    },
  ];
  const fallbackInitiatives = [
    {
      title: "Para Sports Chaitanya Yatra",
      description:
        "A historic statewide awareness movement across all 26 districts to identify new talent and empower differently-abled youth through sports.",
      icon: <FaUsers size={40} />,
      link: "/chaitanya-yatra",
    },
    {
      title: "World-Class Para Sports Stadium",
      description:
        "A state-of-the-art stadium is being established in Visakhapatnam, providing international standard training facilities, hostels, and support centers.",
      icon: <FaTrophy size={40} />,
    },
  ];
  const whyJoinIconSet = [FaBullseye, FaChartLine, FaMedal, FaHandHoldingHeart, FaUsers, FaTrophy];
  const sectionMap = useMemo(
    () =>
      homeSections.reduce((acc, section) => {
        const key = normalizeSectionKey(section?.section_key);
        if (key) acc[key] = section;
        return acc;
      }, {}),
    [homeSections]
  );
  const getSection = (key) => sectionMap[normalizeSectionKey(key)] || null;
  const getSections = (key) =>
    homeSections.filter(
      (section) => normalizeSectionKey(section?.section_key) === normalizeSectionKey(key)
    );
  const sectionImage = (key, defaultPath) =>
    resolveAssetUrl(getSection(key)?.image_path || "", {
      folder: "page-sections",
      defaultPath,
    });
  const parseSectionImageList = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) {
      return input.map((item) => String(item || "").trim()).filter(Boolean);
    }
    if (typeof input !== "string") return [];

    const raw = input.trim();
    if (!raw) return [];

    if (raw.startsWith("[")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => String(item || "").trim()).filter(Boolean);
        }
      } catch (_err) {
        // Ignore parse error and continue with text splitting fallback.
      }
    }

    if (raw.includes("\n")) {
      return raw.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
    }
    if (raw.includes("|")) {
      return raw.split("|").map((item) => item.trim()).filter(Boolean);
    }
    if (raw.includes(",")) {
      return raw.split(",").map((item) => item.trim()).filter(Boolean);
    }

    return [raw];
  };
  const sectionImageList = (section, defaultPath) => {
    const buckets = [
      section?.image_path,
      section?.image_paths,
      section?.images,
      section?.gallery_images,
      section?.photos,
    ];
    const merged = buckets.flatMap((bucket) => parseSectionImageList(bucket));
    const uniquePaths = [...new Set(merged)];
    if (!uniquePaths.length) return [defaultPath];
    return uniquePaths.map((path) =>
      resolveAssetUrl(path, {
        folder: "page-sections",
        defaultPath,
      })
    );
  };
  const sectionImagesByKey = (key, defaultPath) => {
    const matched = getSections(key);
    const combined = matched.flatMap((section) => sectionImageList(section, defaultPath));
    const unique = [...new Set(combined.filter(Boolean))];
    return unique.length ? unique : [defaultPath];
  };
  const sectionPrimaryImage = (section, defaultPath = "") => {
    const buckets = [
      section?.image_path,
      section?.image_paths,
      section?.images,
      section?.gallery_images,
      section?.photos,
    ];
    const merged = buckets.flatMap((bucket) => parseSectionImageList(bucket));
    const first = merged.find(Boolean);

    if (!first) {
      return defaultPath
        ? resolveAssetUrl("", {
            folder: "page-sections",
            defaultPath,
          })
        : "";
    }

    return resolveAssetUrl(first, {
      folder: "page-sections",
      defaultPath: defaultPath || first,
    });
  };
  const parseLayoutConfig = (section) => {
    const raw = section?.layout_config;
    let cfg = raw;
    if (typeof raw === "string") {
      try {
        cfg = JSON.parse(raw);
      } catch (_err) {
        cfg = null;
      }
    }
    if (!cfg || typeof cfg !== "object") cfg = {};
    return {
      layout: ["stack", "split"].includes(cfg.layout) ? cfg.layout : "stack",
      mediaPosition: ["top", "bottom", "left", "right"].includes(cfg.media_position)
        ? cfg.media_position
        : "top",
      showHeading: cfg.show_heading !== false,
      showSubheading: cfg.show_subheading !== false,
      showBody: cfg.show_body !== false,
      showImage: cfg.show_image !== false,
      headingAlign: ["left", "center", "right"].includes(cfg.heading_align)
        ? cfg.heading_align
        : "left",
      textAlign: ["left", "center", "right"].includes(cfg.text_align)
        ? cfg.text_align
        : "left",
      desktopSpan: Number(cfg.desktop_span) === 2 ? 2 : 1,
      imageFit: normalizeImageFit(cfg.image_fit),
      imageSize: normalizeImageSize(cfg.image_size),
    };
  };
  const alignClass = (align) =>
    align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  const sectionSubsections = (baseKey, defaultImage = "https://placehold.co/700x400?text=Subsection") =>
    homeSections
      .filter(
        (s) =>
          s?.is_active !== false &&
          new RegExp(`^${String(baseKey || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}_subsection_`, "i").test(
            String(s?.section_key || "")
          )
      )
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
      .map((s) => ({
        sectionKey: String(s.section_key || ""),
        heading: s.heading || "",
        subheading: s.subheading || "",
        body: s.body || "",
        image: sectionPrimaryImage(s, defaultImage),
        ctaLabel: s.cta_label || "",
        ctaLink: s.cta_link || "",
      }));
  const renderSectionHeader = ({
    section,
    cfg,
    images,
    defaultHeading,
    defaultBody,
    fallbackImage,
    wrapClass = "mb-12",
    titleClass = "text-4xl md:text-5xl font-extrabold text-slate-900",
    bodyClass = "text-lg text-slate-600 mt-3 max-w-3xl mx-auto",
    showImageOnlyIfProvided = true,
  }) => {
    const hasImage = Boolean(
      section?.image_path ||
      section?.image_paths ||
      section?.images ||
      section?.gallery_images ||
      section?.photos
    );
    const canShowImage = cfg.showImage && (!showImageOnlyIfProvided || hasImage);
    const isSplit = cfg.layout === "split" || ["left", "right"].includes(cfg.mediaPosition);
    const mediaFirst = cfg.mediaPosition === "top" || cfg.mediaPosition === "left";
    const mediaLast = cfg.mediaPosition === "bottom" || cfg.mediaPosition === "right";
    return (
      <div className={wrapClass}>
        <div className={`${isSplit ? "flex flex-col lg:flex-row gap-8 items-center" : "flex flex-col gap-6"}`}>
          {canShowImage && mediaFirst ? (
            <div className={isSplit ? "lg:w-1/2 w-full" : "w-full"}>
              <SectionImageGallery
                images={images}
                layout={section?.image_layout || "auto"}
                fit={cfg.imageFit}
                size={cfg.imageSize}
                alt={section?.heading || defaultHeading}
                fallbackSrc={fallbackImage}
              />
            </div>
          ) : null}
          <div className={`${isSplit ? "lg:w-1/2 w-full" : "w-full"} ${alignClass(cfg.textAlign)}`}>
            {cfg.showSubheading && section?.subheading ? (
              <p className={`text-sm font-semibold text-blue-700 uppercase tracking-wide ${alignClass(cfg.headingAlign)}`}>
                {section.subheading}
              </p>
            ) : null}
            {cfg.showHeading ? (
              <h2 className={`${titleClass} ${alignClass(cfg.headingAlign)}`}>
                {section?.heading || defaultHeading}
              </h2>
            ) : null}
            {cfg.showBody ? (
              <p className={bodyClass}>
                {section?.body || defaultBody}
              </p>
            ) : null}
          </div>
          {canShowImage && mediaLast ? (
            <div className={isSplit ? "lg:w-1/2 w-full" : "w-full"}>
              <SectionImageGallery
                images={images}
                layout={section?.image_layout || "auto"}
                fit={cfg.imageFit}
                size={cfg.imageSize}
                alt={section?.heading || defaultHeading}
                fallbackSrc={fallbackImage}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  };
  const withVersion = (url, version) => {
    if (!url || !version) return url;
    return `${url}${url.includes("?") ? "&" : "?"}v=${encodeURIComponent(String(version))}`;
  };
  const heroSection = getSection("home_hero");
  const heroHeading = String(activeSlider?.title || heroSection?.heading || "Inspire").trim() || "Inspire";
  const heroDescription = String(activeSlider?.description || heroSection?.body || "").trim();
  const heroImage = activeSlider
    ? resolveAssetUrl(activeSlider.image, {
        folder: "sliders",
        defaultPath: "/images/hero-background.jpg",
      })
    : sectionImage("home_hero", "/images/hero-background.jpg");
  const heroFitMode = normalizeSliderFitMode(
    activeSlider?.fit_mode || activeSlider?.fitMode || "cover"
  );
  const heroSectionClass =
    "relative overflow-hidden text-white w-full h-[280px] sm:h-[340px] md:h-[420px] lg:h-[500px] xl:h-[580px] bg-slate-900";
  const heroBackdropClass =
    heroFitMode === "cover"
      ? "absolute inset-0 z-0 block w-full h-full object-cover opacity-10 blur-[4px] scale-110"
      : "absolute inset-0 z-0 block w-full h-full object-cover opacity-10 blur-[6px] scale-110";
  const heroImageClass =
    heroFitMode === "cover"
      ? "relative z-10 block w-full h-full object-contain"
      : heroFitMode === "auto"
        ? "relative z-10 block w-full h-full object-contain"
        : "relative z-10 block w-full h-full object-contain";
  const aboutSection = getSection("home_about");
  const aboutCfg = parseLayoutConfig(aboutSection);
  const aboutImages = sectionImageList(aboutSection, "https://placehold.co/1200x700?text=Who+We+Are");
  const aboutSummaryText = summarizeText(
    aboutSection?.body ||
      "Founded in 2015, the Para Sports Association of Andhra Pradesh ( Para Sports Accociation of Andhra Pradesh) is the state's sole organization dedicated to the welfare and advancement of divyang athletes. Our mission is to empower these athletes through world-class training, resources, and opportunities, fostering success on national and international stages."
  );
  const impactSection = getSection("home_impact");
  const impactCfg = parseLayoutConfig(impactSection);
  const impactImages = sectionImageList(impactSection, "https://placehold.co/1200x700?text=Impact");
  const dscSection = getSection("home_dsc");
  const dscCfg = parseLayoutConfig(dscSection);
  const dscImages = sectionImageList(dscSection, "/images/mega-dsc-achievement.jpg");
  const policySection = getSection("home_policy");
  const policyCfg = parseLayoutConfig(policySection);
  const policyImages = sectionImageList(policySection, "https://placehold.co/1200x700?text=Policy");
  const prideSection = getSection("home_pride");
  const prideCfg = parseLayoutConfig(prideSection);
  const prideImages = sectionImagesByKey("home_pride", "/images/pm-meet.jpg");
  const timelineSection = getSection("home_timeline");
  const timelineCfg = parseLayoutConfig(timelineSection);
  const timelineImages = sectionImageList(timelineSection, "https://placehold.co/1200x700?text=Timeline");
  const leadershipSection = getSection("home_leadership");
  const leadershipCfg = parseLayoutConfig(leadershipSection);
  const leadershipImages = sectionImageList(leadershipSection, "https://placehold.co/1200x700?text=Leadership");
  const championsSection = getSection("home_champions");
  const championsCfg = parseLayoutConfig(championsSection);
  const championsImages = sectionImageList(championsSection, "https://placehold.co/1200x700?text=Champions");
  const showChampionsSection = Boolean(championsSection);
  const initiativesSection = getSection("home_initiatives");
  const initiativesCfg = parseLayoutConfig(initiativesSection);
  const initiativesImages = sectionImageList(initiativesSection, "https://placehold.co/1200x700?text=Initiatives");
  const visionSection = getSection("home_vision");
  const visionCfg = parseLayoutConfig(visionSection);
  const visionImages = sectionImageList(visionSection, "https://placehold.co/1200x700?text=Vision");
  const mediaSection = getSection("home_media");
  const mediaCfg = parseLayoutConfig(mediaSection);
  const mediaImages = sectionImageList(mediaSection, "https://placehold.co/1200x700?text=Media");
  const ctaSection = getSection("home_cta");
  const ctaCfg = parseLayoutConfig(ctaSection);
  const ctaImages = sectionImageList(ctaSection, "https://placehold.co/1200x700?text=CTA");

  const whyJoinSections = homeSections
    .filter((s) => s?.is_active !== false && /^why_join_/i.test(String(s?.section_key || "")))
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
  const whyJoinMeta = whyJoinSections.find(
    (s) => String(s.section_key || "").toLowerCase() === "why_join_meta"
  );
  const whyJoinCards = whyJoinSections
    .filter((s) => String(s.section_key || "").toLowerCase() !== "why_join_meta")
    .map((s, idx) => {
      const Icon = whyJoinIconSet[idx % whyJoinIconSet.length];
      return {
        title: s.heading || `Why Join ${idx + 1}`,
        description: s.body || s.subheading || "",
        icon: <Icon size={32} />,
      };
    });
  const finalWhyJoinCards = whyJoinCards.length ? whyJoinCards : fallbackWhyJoinCards;
  const genericHomeSections = homeSections
    .filter((s) => isHomeGenericSectionKey(s?.section_key))
    .filter((s) => s?.is_active !== false)
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .filter((section, index, arr) => {
      const key = normalizeSectionKey(section?.section_key);
      if (!key) return true;
      return arr.findIndex((item) => normalizeSectionKey(item?.section_key) === key) === index;
    });
  const genericSectionsWithConfig = genericHomeSections.map((section) => ({
    section,
    cfg: parseLayoutConfig(section),
  }));
  const timelineItems = homeSections
    .filter((s) => s?.is_active !== false && /^home_timeline_item_/i.test(String(s?.section_key || "")))
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((s) => ({
      year: s.heading || "-",
      count: Number(s.body || 0),
      label: s.subheading || "",
    }));
  const finalTimelineItems = timelineItems.length ? timelineItems : fallbackTimelineItems;
  const leadershipCards = homeSections
    .filter((s) => s?.is_active !== false && /^home_leadership_card_/i.test(String(s?.section_key || "")))
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((s) => ({
      name: s.heading || "",
      role: s.subheading || "",
      message: s.body || "",
      imageSrc: resolveAssetUrl(s.image_path || "", {
        folder: "page-sections",
        defaultPath: "/images/president.jpg",
      }),
    }));
  const finalLeadershipCards = leadershipCards.length ? leadershipCards : fallbackLeadershipCards;
  const leadershipGridClass =
    finalLeadershipCards.length <= 1
      ? "grid grid-cols-1 max-w-xl mx-auto gap-8"
      : finalLeadershipCards.length === 2
        ? "grid grid-cols-1 md:grid-cols-2 max-w-5xl mx-auto gap-8"
        : "grid grid-cols-1 md:grid-cols-3 gap-8";
  const championCards = homeSections
    .filter((s) => s?.is_active !== false && /^home_champion_card_/i.test(String(s?.section_key || "")))
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((s, idx) => {
      const cfg = parseLayoutConfig(s);
      const imageSide =
        cfg.mediaPosition === "right"
          ? "right"
          : cfg.mediaPosition === "left"
            ? "left"
            : idx % 2 === 0
              ? "left"
              : "right";
      return {
        name: s.heading || "",
        achievement: s.body || s.subheading || "",
        imageSrc: withVersion(
          resolveAssetUrl(s.image_path || "", {
            folder: "page-sections",
            defaultPath: "/images/champion-ravi.jpg",
          }),
          s.updated_at || s.id
        ),
        imageSide,
        headingAlign: cfg.headingAlign,
        textAlign: cfg.textAlign,
        imageFit: cfg.imageFit,
        cardLayout: cfg.layout,
      };
    });
  const finalChampionCards = championCards.length ? championCards : fallbackChampionCards;
  const championCardsLayout = championsCfg.layout === "stack" ? "stack" : "split";
  const initiativeIconSet = [FaUsers, FaTrophy, FaChartLine, FaBullseye];
  const initiatives = homeSections
    .filter((s) => s?.is_active !== false && /^home_initiative_card_/i.test(String(s?.section_key || "")))
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((s, idx) => {
      const cfg = parseLayoutConfig(s);
      const Icon = initiativeIconSet[idx % initiativeIconSet.length];
      return {
        title: s.heading || "",
        description: s.body || s.subheading || "",
        imageSrc: sectionPrimaryImage(s),
        icon: <Icon size={40} />,
        link: s.cta_link || "",
        imageFit: cfg.imageFit,
      };
    });
  const finalInitiatives = initiatives.length ? initiatives : fallbackInitiatives;
  const dscSubsections = sectionSubsections("home_dsc");
  const policySubsections = sectionSubsections("home_policy");
  const timelineSubsections = sectionSubsections("home_timeline");
  const leadershipSubsections = sectionSubsections("home_leadership");
  const championsSubsections = sectionSubsections("home_champions");
  const initiativesSubsections = sectionSubsections("home_initiatives");
  const visionSubsections = sectionSubsections("home_vision");
  const mediaSubsections = sectionSubsections("home_media");
  const ctaSubsections = sectionSubsections("home_cta");
  const prideSubsections = sectionSubsections("home_pride");
  const impactCardIconSet = [FaTrophy, FaMedal, FaUsers, FaGlobeAsia, FaChartLine, FaBullseye];
  const impactCards = homeSections
    .filter((s) => s?.is_active !== false && /^home_impact_card_/i.test(String(s?.section_key || "")))
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((s, idx) => {
      const Icon = impactCardIconSet[idx % impactCardIconSet.length];
      return {
        icon: <Icon size={40} />,
        value: s.subheading || s.body || "0",
        label: s.heading || `Impact ${idx + 1}`,
      };
    });
  const finalImpactCards = impactCards.length
    ? impactCards
    : [
        { icon: <FaTrophy size={40} />, value: liveNationalMedals, label: "National Medals" },
        { icon: <FaMedal size={40} />, value: liveInternationalMedals, label: "International Medals" },
        { icon: <FaUsers size={40} />, value: "2", label: "Paralympians" },
        { icon: <FaGlobeAsia size={40} />, value: "5", label: "Asian Para Games Athletes (3 Silver)" },
      ];
  const visionPoints = homeSections
    .filter((s) => s?.is_active !== false && /^home_vision_point_/i.test(String(s?.section_key || "")))
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((s) => s.body || s.heading || "")
    .filter(Boolean);
  const openUpdateEvents = useMemo(
    () =>
      (Array.isArray(publicEvents) ? publicEvents : [])
        .filter((ev) => String(ev?.registration || "").trim().toLowerCase() === "open")
        .sort((a, b) => parsePublicEventDate(b) - parsePublicEventDate(a))
        .slice(0, 3),
    [publicEvents]
  );
  const updatesMeta = useMemo(
    () =>
      (Array.isArray(updatesSections) ? updatesSections : []).find(
        (s) => String(s?.section_key || "").toLowerCase() === "updates_results_meta"
      ) || null,
    [updatesSections]
  );
  const latestResultSheets = useMemo(
    () =>
      (Array.isArray(updatesSections) ? updatesSections : [])
        .filter((s) => s?.is_active !== false)
        .filter((s) => /^updates_result_sheet_/i.test(String(s?.section_key || "")))
        .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0))
        .slice(0, 2),
    [updatesSections]
  );

  return (
    <div className="bg-white">
      <Navbar />
      <section className="border-b border-emerald-200 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-5 py-3 text-center">
          <h2 className="text-base sm:text-lg md:text-xl font-extrabold text-slate-900 leading-tight">
            Para Sports Association of Andhra Pradesh
          </h2>
          <p className="mt-1 text-xs sm:text-sm font-semibold text-emerald-900">
            Affiliated to Paralympic Committee of India
          </p>
          <p className="text-xs sm:text-sm font-semibold text-amber-900">
            Recognized by Sports Authority of Andhra Pradesh
          </p>
        </div>
      </section>

      {/* ===== HERO SECTION ===== */}
      <section className={heroSectionClass}>
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          className={heroBackdropClass}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/images/hero-background.jpg";
          }}
        />
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          className={heroImageClass}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/images/hero-background.jpg";
          }}
        />
        <div className="absolute inset-0 z-20 bg-gradient-to-t from-black/70 via-black/35 to-black/10" />
        <div className="absolute inset-x-0 top-0 z-30 px-3 sm:px-4 lg:px-5 pt-3 sm:pt-4 pointer-events-none">
          <div className="mx-auto max-w-[1400px] flex items-start justify-between gap-3">
            <div className="max-w-3xl rounded-xl border border-white/30 bg-black/40 px-3 py-2 sm:px-4 sm:py-3 backdrop-blur-sm">
              <h1
                className="text-left text-base sm:text-lg md:text-2xl lg:text-3xl font-extrabold tracking-tight leading-snug text-white"
                title={heroHeading}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {heroHeading}
              </h1>
            </div>
            {sliders.length > 1 && (
              <div className="pointer-events-auto mt-1 flex items-center gap-2 rounded-full border border-white/30 bg-black/35 px-2 py-1 backdrop-blur-sm">
                {sliders.map((item, idx) => (
                  <button
                    key={item.id || idx}
                    type="button"
                    onClick={() => setCurrentSlide(idx)}
                    className={`h-2.5 rounded-full transition-all ${
                      idx === currentSlide ? "w-8 bg-blue-500" : "w-2.5 bg-white/60"
                    }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        {heroDescription ? (
          <div className="absolute inset-x-0 bottom-3 sm:bottom-4 z-30 px-3 sm:px-4 lg:px-5 pointer-events-none">
            <div className="mx-auto max-w-4xl rounded-xl border border-white/25 bg-black/35 px-4 py-2 sm:px-5 sm:py-3 backdrop-blur-sm">
              <p className="text-center text-sm sm:text-base md:text-lg text-white leading-relaxed">
                {heroDescription}
              </p>
            </div>
          </div>
        ) : null}
      </section>

      {genericHomeSections.length > 0 && (
        <section className="py-16 bg-sky-50 border-y border-sky-100">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {genericSectionsWithConfig.map(({ section, cfg }, index) => {
                    const textAlignClass = alignClass(cfg.textAlign);
                    const headingAlignClass = alignClass(cfg.headingAlign);
                    const isSplit = cfg.layout === "split" || ["left", "right"].includes(cfg.mediaPosition);
                    const mediaFirst = cfg.mediaPosition === "top" || cfg.mediaPosition === "left";
                    const mediaLast = cfg.mediaPosition === "bottom" || cfg.mediaPosition === "right";
                    return (
                  <article
                    key={section.id || section.section_key || index}
                    className={`bg-slate-50 rounded-2xl overflow-hidden shadow-sm border border-slate-100 h-full ${
                      cfg.desktopSpan === 2 ? "lg:col-span-2" : ""
                    }`}
                  >
                    <div
                      className={`${
                        isSplit ? "flex flex-col lg:flex-row" : "flex flex-col"
                      }`}
                    >
                      {cfg.showImage && mediaFirst ? (
                        <div className={isSplit ? "lg:w-1/2" : ""}>
                          <SectionImageGallery
                            images={sectionImageList(section, "https://placehold.co/1200x700?text=Home+Section")}
                            layout={section.image_layout || "auto"}
                            fit={cfg.imageFit}
                            size={cfg.imageSize}
                            alt={section.heading}
                            fallbackSrc="https://placehold.co/1200x700?text=Home+Section"
                          />
                        </div>
                      ) : null}
                      <div className={`p-6 md:p-8 ${isSplit ? "lg:w-1/2" : ""} ${textAlignClass}`}>
                        {cfg.showSubheading && section.subheading ? (
                          <p className={`text-sm font-semibold text-blue-700 uppercase tracking-wide ${headingAlignClass}`}>{section.subheading}</p>
                        ) : null}
                        {cfg.showHeading ? (
                          <h2 className={`text-2xl md:text-3xl font-extrabold text-slate-900 mt-2 ${headingAlignClass}`}>
                            {section.heading}
                          </h2>
                        ) : null}
                        {cfg.showBody && section.body ? (
                          <p className="text-slate-600 mt-4 leading-relaxed whitespace-pre-line">{section.body}</p>
                        ) : null}
                      </div>
                      {cfg.showImage && mediaLast ? (
                        <div className={isSplit ? "lg:w-1/2" : ""}>
                          <SectionImageGallery
                            images={sectionImageList(section, "https://placehold.co/1200x700?text=Home+Section")}
                            layout={section.image_layout || "auto"}
                            fit={cfg.imageFit}
                            size={cfg.imageSize}
                            alt={section.heading}
                            fallbackSrc="https://placehold.co/1200x700?text=Home+Section"
                          />
                        </div>
                      ) : null}
                    </div>
                  </article>
                    );
                  })}
            </div>
          </div>
        </section>
      )}

      {/* ===== PRIDE OF THE NATION ===== */}
      <section className="py-20 bg-orange-50 border-y border-orange-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-3 bg-orange-100 rounded-full mb-4 text-orange-600">
              <FaFlag size={32} />
            </div>
            {prideCfg.showHeading ? (
              <h2 className={`text-3xl md:text-4xl font-extrabold text-slate-900 ${alignClass(prideCfg.headingAlign)}`}>
                {prideSection?.heading || "Pride of the Nation"}
              </h2>
            ) : null}
            {prideCfg.showBody ? (
              <p className={`text-lg text-slate-600 mt-3 max-w-3xl mx-auto ${alignClass(prideCfg.textAlign)}`}>
                {prideSection?.body ||
                  "A historic moment for Andhra Pradesh as our Asian Para Games medalists were felicitated by the Hon'ble Prime Minister of India."}
              </p>
            ) : null}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
             <div className="space-y-6">
                <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-orange-500">
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Recognition of Excellence</h3>
                    <p className="text-slate-600">Our athletes' dedication was recognized at the highest level, inspiring millions across the nation.</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-green-500">
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Asian Para Games Triumph</h3>
                    <p className="text-slate-600">With 5 athletes representing India and securing 3 Silver medals, Andhra Pradesh made its mark on the continental stage.</p>
                </div>
                 <div className="bg-white p-6 rounded-2xl shadow-md border-l-4 border-blue-500">
                    <h3 className="text-xl font-bold text-slate-800 mb-2">A Vision for 2028</h3>
                    <p className="text-slate-600">This recognition fuels our mission to nurture more champions for the Los Angeles 2028 Paralympics.</p>
                </div>
             </div>
             {prideCfg.showImage ? (
               <div className="h-full min-h-[400px] rounded-2xl overflow-hidden shadow-2xl bg-white">
                 <SectionImageGallery
                   images={prideImages}
                   layout={prideSection?.image_layout || "auto"}
                   fit={prideCfg.imageFit}
                   size={prideCfg.imageSize}
                   alt="Asian Para Games Medalists with Prime Minister"
                   fallbackSrc="https://placehold.co/800x800?text=Photo+with+PM"
                 />
                 <div className="px-5 py-4 bg-gradient-to-r from-slate-900 to-slate-700">
                   <p className="text-white font-semibold text-lg">Hon'ble PM with our Champions</p>
                 </div>
               </div>
             ) : null}
          </div>
          <SectionSubsectionGrid items={prideSubsections} />
        </div>
      </section>

      {/* ===== OUR IMPACT SECTION ===== */}
      <section className="py-20 bg-emerald-50 border-y border-emerald-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
          <div className="mb-12">
            <div
              className={`${
                impactCfg.layout === "split" || ["left", "right"].includes(impactCfg.mediaPosition)
                  ? "flex flex-col lg:flex-row gap-8 items-center"
                  : "flex flex-col gap-6"
              }`}
            >
              {impactCfg.showImage && (impactSection?.image_path || impactSection?.image_paths) &&
              (impactCfg.mediaPosition === "top" || impactCfg.mediaPosition === "left") ? (
                <div
                  className={`${
                    impactCfg.layout === "split" || ["left", "right"].includes(impactCfg.mediaPosition)
                      ? "lg:w-1/2"
                      : "w-full"
                  }`}
                >
                  <SectionImageGallery
                    images={impactImages}
                    layout={impactSection?.image_layout || "auto"}
                    fit={impactCfg.imageFit}
                    size={impactCfg.imageSize}
                    alt={impactSection?.heading || "Our Impact"}
                    fallbackSrc="https://placehold.co/1200x700?text=Impact"
                  />
                </div>
              ) : null}
              <div
                className={`${
                  impactCfg.layout === "split" || ["left", "right"].includes(impactCfg.mediaPosition)
                    ? "lg:w-1/2"
                    : "w-full"
                } ${alignClass(impactCfg.textAlign)}`}
              >
                {impactCfg.showSubheading && impactSection?.subheading ? (
                  <p className={`text-sm font-semibold text-blue-700 uppercase tracking-wide ${alignClass(impactCfg.headingAlign)}`}>
                    {impactSection.subheading}
                  </p>
                ) : null}
                {impactCfg.showHeading ? (
                  <h2 className={`text-4xl md:text-5xl font-extrabold text-slate-900 ${alignClass(impactCfg.headingAlign)}`}>
                    {impactSection?.heading || "Our Impact in Numbers"}
                  </h2>
                ) : null}
                {impactCfg.showBody ? (
                  <p className="text-lg text-slate-600 mt-3 max-w-2xl mx-auto">
                    {impactSection?.body ||
                      "A decade of dedication, measured in milestones."}
                  </p>
                ) : null}
              </div>
              {impactCfg.showImage && (impactSection?.image_path || impactSection?.image_paths) &&
              (impactCfg.mediaPosition === "bottom" || impactCfg.mediaPosition === "right") ? (
                <div
                  className={`${
                    impactCfg.layout === "split" || ["left", "right"].includes(impactCfg.mediaPosition)
                      ? "lg:w-1/2"
                      : "w-full"
                  }`}
                >
                  <SectionImageGallery
                    images={impactImages}
                    layout={impactSection?.image_layout || "auto"}
                    fit={impactCfg.imageFit}
                    size={impactCfg.imageSize}
                    alt={impactSection?.heading || "Our Impact"}
                    fallbackSrc="https://placehold.co/1200x700?text=Impact"
                  />
                </div>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {finalImpactCards.map((card, idx) => (
              <StatCard
                key={`${card.label}-${idx}`}
                icon={card.icon}
                value={card.value}
                label={card.label}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== HISTORIC ACHIEVEMENT (MEGA DSC) ===== */}
      <section className="py-20 bg-amber-50 border-y border-amber-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              {dscCfg.showSubheading && dscSection?.subheading ? (
                <div className={`inline-block bg-blue-600 text-white text-sm font-bold px-4 py-2 rounded-full mb-4 ${alignClass(dscCfg.headingAlign)}`}>
                  {dscSection.subheading}
                </div>
              ) : null}
              {dscCfg.showHeading ? (
                <h2 className={`text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 ${alignClass(dscCfg.headingAlign)}`}>
                  {dscSection?.heading || "15 Para Athletes Secured Government Jobs"}
                </h2>
              ) : null}
              {dscCfg.showBody ? (
                <p className={`text-lg text-slate-700 mb-6 leading-relaxed ${alignClass(dscCfg.textAlign)}`}>
                  {dscSection?.body ||
                    "We are proud to share a remarkable milestone. Fifteen outstanding Para Sportspersons from Andhra Pradesh have recently secured government jobs as teachers (DSC posts) in Mega DSC 2025. This recognition was granted purely on the basis of their sports merit, without the need for any written examination. It stands as a testament to the unwavering dedication of our athletes and the progressive vision of the Government of Andhra Pradesh."}
                </p>
              ) : null}
              <div className="flex items-center gap-4 text-blue-800 font-semibold">
                <FaBriefcase size={24} />
                <span>Empowering lives beyond the field</span>
              </div>
            </div>
            {dscCfg.showImage ? (
              <div className="lg:w-1/2">
                <SectionImageGallery
                  images={dscImages}
                  layout={dscSection?.image_layout || "auto"}
                  fit={dscCfg.imageFit}
                  size={dscCfg.imageSize}
                  alt="Para athletes receiving appointment orders"
                  fallbackSrc="https://placehold.co/600x400?text=Mega+DSC+Achievement"
                />
              </div>
            ) : null}
          </div>
          <SectionSubsectionGrid items={dscSubsections} />
        </div>
      </section>

      {/* ===== SPORTS POLICY (G.O. NO. 4) ===== */}
      <section className="py-16 bg-blue-50 border-y border-blue-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
          <div className="bg-white border border-blue-100 rounded-2xl shadow-lg p-8 md:p-10">
            <div className="inline-flex items-center gap-2 bg-blue-600 text-white text-xs md:text-sm font-bold px-4 py-2 rounded-full mb-4 uppercase tracking-wide">
              <FaBriefcase />
              Government Policy Update
            </div>
            {renderSectionHeader({
              section: policySection,
              cfg: policyCfg,
              images: policyImages,
              fallbackImage: "https://placehold.co/1200x700?text=Policy",
              defaultHeading: "Sports Policy - G.O. No. 4",
              defaultBody:
                "Government Order No. 4 under the Andhra Pradesh Sports Policy strengthens para sports through inclusive athlete development, classification-aware competition pathways, coaching support, and long-term welfare measures. It recognizes para sportspersons for merit-based opportunities including sports quota jobs in government services, and reinforces structured support from district to international levels.",
              wrapClass: "mb-2",
              titleClass: "text-3xl md:text-4xl font-extrabold text-slate-900",
              bodyClass: "text-slate-700 text-base md:text-lg mt-4 leading-relaxed",
            })}
            <ul className="mt-5 space-y-2 text-slate-700 text-sm md:text-base list-disc pl-5">
              <li>
                Para sportspersons are covered for incentives, cash rewards, and performance-linked recognition at state, national, and international levels.
              </li>
              <li>
                Merit-based employment pathways under sports policy provisions, including government job opportunities for eligible para athletes.
              </li>
              <li>
                Development of para sports infrastructure in Andhra Pradesh, including the proposed dedicated para sports stadium in Visakhapatnam across approximately 22 acres with an estimated outlay of Rs 200 crores.
              </li>
              <li>
                Focus areas include accessibility, athlete classification support, high-performance training, rehabilitation-oriented support, and inclusive sports governance.
              </li>
            </ul>
            {policySection?.cta_link ? (
              <a
                href={policySection?.cta_link}
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-6 text-blue-700 font-semibold hover:text-blue-900 transition-colors"
              >
                {policySection?.cta_label || "Read G.O. No. 4"} &rarr;
              </a>
            ) : null}
            <SectionSubsectionGrid items={policySubsections} />
          </div>
        </div>
      </section>

      {/* ===== ABOUT  Para Sports Accociation of Andhra Pradesh SECTION ===== */}
      <section className="py-20 bg-rose-50 border-y border-rose-100">
        <div
          className={`${
            aboutCfg.desktopSpan === 2 || aboutCfg.layout === "split"
              ? "max-w-[1400px]"
              : "max-w-[1200px]"
          } mx-auto px-4 sm:px-5 lg:px-5`}
        >
          {(() => {
            const hasAboutImage = Boolean(aboutSection?.image_path || aboutSection?.image_paths);
            const isSplit = aboutCfg.layout === "split" || ["left", "right"].includes(aboutCfg.mediaPosition);
            const mediaFirst = aboutCfg.mediaPosition === "top" || aboutCfg.mediaPosition === "left";
            const mediaLast = aboutCfg.mediaPosition === "bottom" || aboutCfg.mediaPosition === "right";
            return (
              <div className={`${isSplit ? "flex flex-col lg:flex-row items-center gap-10" : "flex flex-col gap-8"}`}>
                {aboutCfg.showImage && mediaFirst ? (
                  <div className={isSplit ? "lg:w-1/2" : "w-full max-w-3xl mx-auto"}>
                    {hasAboutImage ? (
                      <SectionImageGallery
                        images={aboutImages}
                        layout={aboutSection?.image_layout || "auto"}
                        fit={aboutCfg.imageFit}
                        size={aboutCfg.imageSize}
                        alt={aboutSection?.heading || "Who We Are"}
                        fallbackSrc="https://placehold.co/1200x700?text=Who+We+Are"
                      />
                    ) : (
                      <img src="/images/parasports.png" alt=" Para Sports Accociation of Andhra Pradesh emblem" className="h-20 w-auto mx-auto" />
                    )}
                  </div>
                ) : null}
                <div className={`${isSplit ? "lg:w-1/2" : "w-full"} ${alignClass(aboutCfg.textAlign)}`}>
                  {aboutCfg.showHeading ? (
                    <h2 className={`text-4xl md:text-5xl font-extrabold text-slate-900 ${alignClass(aboutCfg.headingAlign)}`}>
                      {aboutSection?.heading || "Who We Are"}
                    </h2>
                  ) : null}
                  {aboutCfg.showBody ? (
                    <p className="text-lg text-slate-600 mt-4 max-w-3xl mx-auto">
                      {aboutSummaryText}
                    </p>
                  ) : null}
                  <Link
                    to="/about-us"
                    className="mt-8 inline-block font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Discover Our Full Story &rarr;
                  </Link>
                </div>
                {aboutCfg.showImage && mediaLast ? (
                  <div className={isSplit ? "lg:w-1/2" : "w-full max-w-3xl mx-auto"}>
                    {hasAboutImage ? (
                      <SectionImageGallery
                        images={aboutImages}
                        layout={aboutSection?.image_layout || "auto"}
                        fit={aboutCfg.imageFit}
                        size={aboutCfg.imageSize}
                        alt={aboutSection?.heading || "Who We Are"}
                        fallbackSrc="https://placehold.co/1200x700?text=Who+We+Are"
                      />
                    ) : (
                      <img src="/images/parasports.png" alt=" Para Sports Accociation of Andhra Pradesh emblem" className="h-20 w-auto mx-auto" />
                    )}
                  </div>
                ) : null}
              </div>
            );
          })()}
        </div>
      </section>

      {/* ===== MEDAL TALLY TIMELINE ===== */}
      <section className="py-20 bg-cyan-50 border-y border-cyan-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
          {renderSectionHeader({
            section: timelineSection,
            cfg: timelineCfg,
            images: timelineImages,
            fallbackImage: "https://placehold.co/1200x700?text=Timeline",
            defaultHeading: `Medal Tally (2015-${currentYear})`,
            defaultBody: "A decade of consistent growth and dominance.",
            wrapClass: "mb-12",
            titleClass: "text-3xl md:text-4xl font-extrabold text-slate-900",
            bodyClass: "text-lg text-slate-600 mt-3",
          })}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {finalTimelineItems.map((item) => {
              const isStillCountingYear = String(item.year || "").trim() === currentYear;
              const labelText = isStillCountingYear
                ? [item.label, "Still Counting..."].filter(Boolean).join(" - ")
                : item.label;
              return (
                <div key={item.year} className="bg-slate-50 p-4 rounded-lg text-center border border-slate-200">
                  <div className="text-sm text-slate-500 font-bold">{item.year} {labelText ? `(${labelText})` : ''}</div>
                  <div className="text-2xl font-extrabold text-blue-600">{item.count}</div>
                  <div className="text-xs text-slate-400">Medals</div>
                </div>
              );
            })}
            <div className="bg-blue-600 p-4 rounded-lg text-center text-white">
              <div className="text-sm font-bold opacity-80">National Total</div>
              <div className="text-2xl font-extrabold">{liveNationalMedals}</div>
              <div className="text-xs opacity-80">Medals</div>
            </div>
          </div>
          <SectionSubsectionGrid items={timelineSubsections} />
        </div>
      </section>

      {/* ===== LEADERSHIP MESSAGES ===== */}
      <section className="py-20 bg-lime-50 border-y border-lime-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
          {renderSectionHeader({
            section: leadershipSection,
            cfg: leadershipCfg,
            images: leadershipImages,
            fallbackImage: "https://placehold.co/1200x700?text=Leadership",
            defaultHeading: "Leadership Speak",
            defaultBody: "The visionaries guiding our mission.",
            wrapClass: "mb-12",
            titleClass: "text-3xl md:text-4xl font-extrabold text-slate-900",
            bodyClass: "text-lg text-slate-600 mt-3",
          })}
          <div className={leadershipGridClass}>
            {finalLeadershipCards.map((item, idx) => (
              <LeadershipCard
                key={`${item.name}-${idx}`}
                name={item.name}
                role={item.role}
                message={item.message}
                imageSrc={item.imageSrc}
              />
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/messages" className="inline-block border-2 border-blue-600 text-blue-600 font-bold py-3 px-8 rounded-full hover:bg-blue-600 hover:text-white transition-colors">
              Read Full Messages
            </Link>
          </div>
          <SectionSubsectionGrid items={leadershipSubsections} />
        </div>
      </section>

      {/* ===== OUR CHAMPIONS SECTION ===== */}
      {showChampionsSection ? (
        <section className="py-20 bg-teal-50 border-y border-teal-100">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
            {renderSectionHeader({
              section: championsSection,
              cfg: championsCfg,
              images: championsImages,
              fallbackImage: "https://placehold.co/1200x700?text=Champions",
              defaultHeading: "Our Champions",
              defaultBody: "Meet the heroes who inspire us and carry the flag of Andhra Pradesh with pride.",
              wrapClass: "mb-12",
              titleClass: "text-4xl md:text-5xl font-extrabold text-slate-900",
              bodyClass: "text-lg text-slate-600 mt-3 max-w-2xl mx-auto",
            })}
            <div
              className={
                championCardsLayout === "stack"
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  : "space-y-8"
              }
            >
              {finalChampionCards.map((item, idx) => (
                <ChampionCard
                  key={`${item.name}-${idx}`}
                  name={item.name}
                  achievement={item.achievement}
                  imageSrc={item.imageSrc}
                  imageSide={item.imageSide || (idx % 2 === 0 ? "left" : "right")}
                  headingAlign={item.headingAlign || "left"}
                  textAlign={item.textAlign || "left"}
                  imageFit={item.imageFit || championsCfg.imageFit}
                  variant={championCardsLayout === "stack" ? "stack" : item.cardLayout === "stack" ? "stack" : "split"}
                />
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/players" className="inline-block bg-blue-600 text-white font-bold py-3 px-8 rounded-full hover:bg-blue-700 transition-colors shadow-lg">
                View All Medalists
              </Link>
            </div>
            <SectionSubsectionGrid items={championsSubsections} />
          </div>
        </section>
      ) : null}

      {/* ===== KEY INITIATIVES SECTION ===== */}
       <section className="py-20 bg-sky-50 border-y border-sky-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
          {renderSectionHeader({
            section: initiativesSection,
            cfg: initiativesCfg,
            images: initiativesImages,
            fallbackImage: "https://placehold.co/1200x700?text=Initiatives",
            defaultHeading: "Building the Future",
            defaultBody: "Our commitment extends beyond competition, creating lasting opportunities for para athletes.",
            wrapClass: "mb-12",
            titleClass: "text-4xl md:text-5xl font-extrabold text-slate-900",
            bodyClass: "text-lg text-slate-600 mt-3 max-w-2xl mx-auto",
          })}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {finalInitiatives.map((item, idx) => (
              <InitiativeCard
                key={`${item.title}-${idx}`}
                title={item.title}
                description={item.description}
                imageSrc={item.imageSrc}
                icon={item.icon}
                link={item.link}
                imageFit={item.imageFit || initiativesCfg.imageFit}
              />
            ))}
          </div>
          <SectionSubsectionGrid items={initiativesSubsections} />
        </div>
      </section>

      {/* ===== FUTURE VISION ===== */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              {visionCfg.showImage && (visionSection?.image_path || visionSection?.image_paths) ? (
                <div className="mb-6">
                  <SectionImageGallery
                    images={visionImages}
                    layout={visionSection?.image_layout || "auto"}
                    fit={visionCfg.imageFit}
                    size={visionCfg.imageSize}
                    alt={visionSection?.heading || "Future Vision"}
                    fallbackSrc="https://placehold.co/1200x700?text=Vision"
                  />
                </div>
              ) : null}
              {visionCfg.showHeading ? (
                <h2 className={`text-3xl md:text-4xl font-extrabold mb-6 ${alignClass(visionCfg.headingAlign)}`}>
                  {visionSection?.heading || "Future Vision: Towards Paralympic Glory"}
                </h2>
              ) : null}
              {visionCfg.showBody ? (
                <p className={`text-lg opacity-90 mb-6 ${alignClass(visionCfg.textAlign)}`}>
                  {visionSection?.body ||
                    "The Para Sports Association of Andhra Pradesh stands at the cusp of greatness. Building on a decade of remarkable achievements, we are committed to expanding our impact."}
                </p>
              ) : null}
              <ul className="space-y-4">
                {(visionPoints.length
                  ? visionPoints
                  : [
                      "Secure Paralympic medals in future games, building on the 2024 Paris milestone.",
                      "Increase athlete participation across diverse sports and elevate Andhra Pradesh to the top national ranking.",
                      "Expand coaching camps to underserved regions and establish international training partnerships.",
                    ]
                ).map((point, idx) => (
                  <li key={`${idx}-${point.slice(0, 20)}`} className="flex items-start gap-3">
                    <FaBullseye className="mt-1 text-yellow-400" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:w-1/2 bg-blue-800 p-8 rounded-2xl shadow-xl border border-blue-700">
              <h3 className="text-2xl font-bold mb-4 text-yellow-400">Our Commitment</h3>
              <p className="italic text-lg leading-relaxed">
                "Every medal tells a story, but every untapped talent holds a dream. Together, we will discover them, train them, and help them shine on the world stage."
              </p>
              <div className="mt-6 text-right font-semibold">-  Para Sports Accociation of Andhra Pradesh Team</div>
            </div>
          </div>
          <SectionSubsectionGrid items={visionSubsections} />
        </div>
      </section>
      
      {/* ===== LATEST ACTIVITIES ===== */}
      {activities.length > 0 && (
        <section id="events" className="py-20 bg-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900">
                Latest Activities
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-3">
                Upcoming events and recent achievements from our community.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {activities.slice(0, 3).map((activity) => (
                <div key={activity.id} className="bg-slate-50 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                       <span className="text-sm font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                        {activity.category || "Event"}
                      </span>
                      <span className="text-sm text-slate-500">
                        {new Date(activity.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{activity.title}</h3>
                    <p className="text-gray-600 line-clamp-3">{activity.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== UPDATES & RESULTS PREVIEW ===== */}
      <section className="py-20 bg-indigo-50 border-y border-indigo-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
              {updatesMeta?.heading || "Updates & Results"}
            </h2>
            <p className="text-lg text-slate-600 mt-3 max-w-3xl mx-auto">
              {updatesMeta?.subheading ||
                "Track live event updates and check the latest published result sheets."}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <article className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
                  <FaBroadcastTower size={20} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Live Updates</h3>
              </div>

              {openUpdateEvents.length > 0 ? (
                <ul className="space-y-3">
                  {openUpdateEvents.map((event) => (
                    <li key={event.id} className="p-3 rounded-lg border border-blue-100 bg-blue-50">
                      <p className="font-semibold text-slate-900">{event.title || "Event"}</p>
                      <p className="text-sm text-slate-600 mt-1">
                        {formatShortEventDateRange(event.date || event.event_date, event.event_end_date)}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500">No live events currently open.</p>
              )}

              <div className="mt-6">
                <Link
                  to="/updates-results"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                >
                  <FaBroadcastTower size={16} />
                  View Live Updates
                </Link>
              </div>
            </article>

            <article className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center">
                  <FaFileAlt size={20} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Result Sheets</h3>
              </div>

              {latestResultSheets.length > 0 ? (
                <ul className="space-y-3">
                  {latestResultSheets.map((sheet, idx) => (
                    <li key={sheet.id || `${sheet.section_key || "sheet"}-${idx}`} className="p-3 rounded-lg border border-indigo-100 bg-indigo-50">
                      <p className="font-semibold text-slate-900">{sheet.heading || "Result Sheet"}</p>
                      {sheet.subheading ? (
                        <p className="text-xs uppercase tracking-wide text-indigo-700 mt-1">{sheet.subheading}</p>
                      ) : null}
                      {sheet.body ? (
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">{sheet.body}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500">No result sheets published yet.</p>
              )}

              <div className="mt-6">
                <Link
                  to="/updates-results"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
                >
                  <FaFileAlt size={16} />
                  View Result Sheets
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ===== MEDIA & GALLERY ===== */}
      <section className="py-20 bg-amber-50 border-y border-amber-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5 text-center">
          {renderSectionHeader({
            section: mediaSection,
            cfg: mediaCfg,
            images: mediaImages,
            fallbackImage: "https://placehold.co/1200x700?text=Media",
            defaultHeading: "Media & Gallery",
            defaultBody: "",
            wrapClass: "mb-12",
            titleClass: "text-3xl md:text-4xl font-extrabold text-slate-900",
            bodyClass: "text-lg text-slate-600 mt-3",
          })}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Link to="/news" className="group block bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 bg-blue-100 flex items-center justify-center text-blue-500 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                 <FaNewspaper size={64} />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">In the News</h3>
                <p className="text-slate-600 mt-2">Read articles and press clippings about our journey.</p>
              </div>
            </Link>
            <Link to="/gallery" className="group block bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="h-48 bg-purple-100 flex items-center justify-center text-purple-500 group-hover:bg-purple-600 group-hover:text-white transition-colors">
                 <FaImages size={64} />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-slate-900 group-hover:text-purple-600 transition-colors">Photo Gallery</h3>
                <p className="text-slate-600 mt-2">Explore moments of triumph and training.</p>
              </div>
            </Link>
          </div>
          <SectionSubsectionGrid items={mediaSubsections} />
        </div>
      </section>

      {/* ===== WHY JOIN PARA SPORTS ===== */}
      <section className="py-20 bg-emerald-50 border-y border-emerald-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-5">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
              {whyJoinMeta?.heading || "Why Join Para Sports?"}
            </h2>
            <p className="text-lg text-slate-600 mt-3 max-w-3xl mx-auto">
              {whyJoinMeta?.body ||
                "Para sports build confidence, performance, and life-changing opportunities through structured support."}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {finalWhyJoinCards.map((item) => (
              <div key={item.title} className="bg-slate-50 rounded-2xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition-shadow">
                <div className="text-blue-600 mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-slate-900">{item.title}</h3>
                <p className="text-slate-600 mt-2">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CALL TO ACTION SECTION ===== */}
      <section className="bg-blue-600">
        <div className="max-w-[1200px] mx-auto text-center text-white px-4 sm:px-5 lg:px-5 py-20">
          {ctaCfg.showImage && (ctaSection?.image_path || ctaSection?.image_paths) ? (
            <div className="max-w-3xl mx-auto mb-8">
              <SectionImageGallery
                images={ctaImages}
                layout={ctaSection?.image_layout || "auto"}
                fit={ctaCfg.imageFit}
                size={ctaCfg.imageSize}
                alt={ctaSection?.heading || "Call to Action"}
                fallbackSrc="https://placehold.co/1200x700?text=CTA"
              />
            </div>
          ) : null}
          {ctaCfg.showHeading ? (
            <h2 className={`text-4xl md:text-5xl font-extrabold ${alignClass(ctaCfg.headingAlign)}`}>
              {ctaSection?.heading || "Be a Part of the Journey"}
            </h2>
          ) : null}
          {ctaCfg.showBody ? (
            <p className={`text-lg opacity-90 mt-4 max-w-2xl mx-auto ${alignClass(ctaCfg.textAlign)}`}>
              {ctaSection?.body ||
                "Your support can change lives. Help us discover and nurture the next generation of para-champions from Andhra Pradesh."}
            </p>
          ) : null}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
             <Link
              to={ctaSection?.cta_link || "/donate"}
              className="bg-yellow-500 text-slate-900 font-bold text-lg px-8 py-3 rounded-lg shadow-lg hover:bg-yellow-400 transform hover:scale-105 transition-all duration-300"
            >
              {ctaSection?.cta_label || "Donate Now"}
            </Link>
            <Link
              to="/volunteer"
              className="bg-transparent border-2 border-white text-white font-bold text-lg px-8 py-3 rounded-lg shadow-lg hover:bg-white hover:text-slate-900 transform hover:scale-105 transition-all duration-300"
            >
              Volunteer
            </Link>
          </div>
          <SectionSubsectionGrid items={ctaSubsections} />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
